import { Component, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {MatCheckboxModule} from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { WorkspaceService } from '../../tabs/workspaces-tab/services/workspace.service';
import { LoadingSpinnerComponent } from "../../../../shared/components/loading-spinner/loading-spinner.component";
import { DataStoreProvisionStatusType } from '../../tabs/data-lakes-tab/models/data-store-provision-status';
import { DataStoreService } from '../../tabs/data-lakes-tab/services/data-store.service';
import { Workspace } from '../../tabs/workspaces-tab/models/workspace.model';
import { TenantContextService } from '../../services/tenant-context.service';
import { WorkspaceCreationStageType } from '../../tabs/workspaces-tab/models/workspace-creation-stage-status.model';
import { CreateWorkspaceJobRequest } from '../../tabs/workspaces-tab/models/create-workspace-job.request';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-add-workspace-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatAutocompleteModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatDividerModule,
    LoadingSpinnerComponent
],
  templateUrl: './add-workspace-dialog.component.html',
  styleUrl: './add-workspace-dialog.component.css'
})
export class AddWorkspaceDialogComponent implements OnInit, OnDestroy{
  // subscription for a subject to be able to change the workspace creation stage for a more dynamic loading message based on stage
  static workspaceCreationStage$ = new Subject<{ tenantId: string, creationStage: WorkspaceCreationStageType }>();
  private workspaceCreationStageSub?: Subscription;
  
  workspaceService = inject(WorkspaceService);
  dataStoreService = inject(DataStoreService);
  tenantContextService = inject(TenantContextService);

  activeDataLakes = this.dataStoreService.dataLakes().filter(datalake => datalake.provisionStatusTypeId === DataStoreProvisionStatusType.Created);
  activeLandingZones = this.dataStoreService.landingZones().filter(landingZone => landingZone.provisionStatusTypeId === DataStoreProvisionStatusType.Created);
  
  addWorkspaceForm: FormGroup;

  isSubmitting = signal<boolean>(false);
  submissionErrorMessage = signal<string>("");

  workspaceCreationStateEnum = WorkspaceCreationStageType;

  workspaceCreationStage = signal<WorkspaceCreationStageType>(WorkspaceCreationStageType.None);

  constructor (private dialogRef: MatDialogRef<AddWorkspaceDialogComponent>, private fb: FormBuilder) {
    this.addWorkspaceForm = this.fb.group({
      privateAccessToWorkspace: [false],
      workspaceAlias: [null, [
        // regex pattern validation
        // 1. Cannot have leading or trailing spaces
        // 2. 3-128 chars or empty
        // 3. Not all spaces (includes all white space characters)
        Validators.pattern(/^$|^(?! )(?!.* $)(?!\s+$).{3,128}$/)
      ]]
    });
  }

  ngOnInit() {
    this.workspaceCreationStageSub = AddWorkspaceDialogComponent.workspaceCreationStage$.subscribe(({ tenantId, creationStage }) => {
      if (tenantId === this.tenantContextService.tenant()?.tenantId) {
        this.workspaceCreationStage.set(creationStage);
      }
    });
  }

  onSubmit(): void {
    this.submissionErrorMessage.set("");

    if (this.addWorkspaceForm.valid) {
      this.isSubmitting.set(true);
      this.workspaceCreationStage.set(WorkspaceCreationStageType.Creating);

      const tenantId = this.tenantContextService.tenant()?.tenantId;
      if (!tenantId) return;

      const usePrivateWorkspace: boolean = this.addWorkspaceForm.get('privateAccessToWorkspace')?.value;
      let workspaceAlias: string | null = this.addWorkspaceForm.get('workspaceAlias')?.value;

      if (workspaceAlias === "") {
        workspaceAlias = null;
      }

      const request: CreateWorkspaceJobRequest = {
        tenantId,
        workspaceAlias,
        usePrivateWorkspace
      }

      this.workspaceService.createWorkspace(request).subscribe({
        next: (workspace: Workspace) => {
          this.dialogRef.close(workspace);
          this.workspaceCreationStage.set(WorkspaceCreationStageType.None);
          this.isSubmitting.set(false);
        },
        error: (error: string) => {
          this.isSubmitting.set(false);
          this.workspaceCreationStage.set(WorkspaceCreationStageType.None);
          this.submissionErrorMessage.set(error);
        }
      });
    }
  }

  // display default browser popup on unloading page during form submission
  // does not warn navigating to another page of neuro utils as it is a SPA and would require a route guard for it
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.isSubmitting()) {
      $event.preventDefault();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.workspaceCreationStageSub?.unsubscribe();
  }
}
